/**
 * BGP AS Path Graph Builder
 *
 * This module builds a graph visualization structure from BGP routing data.
 * The graph represents the hierarchical relationship between:
 * - Prefixes (announced IP ranges)
 * - Origin ASNs (autonomous systems that announce the prefixes)
 * - Upstream ASNs (transit providers that route traffic to origins)
 *
 * Graph structure flows: Upstreams (left) → Origins (right) → Prefixes (far right)
 */

import { toJS } from 'mobx';

/**
 * Parse vantage point key string into components
 * Format: "dataset|collector|peer_asn|peer_ip"
 * Example: "route-views|route-views4|1351|132.198.255.254"
 */
export const parseVpKey = (vpKey) => {
  const [dataset, collector, peer_asn, peer_ip] = vpKey.split('|');
  return {
    dataset,
    collector,
    peer_asn: parseInt(peer_asn, 10),
    peer_ip
  };
};

/**
 * Create hop metadata for a route
 */
const createHop = (nodeId, routeId, route, prefix) => {
  const { vp_key, as_path, is_pruned } = route;
  const vpInfo = parseVpKey(vp_key);
  const routeIndex = parseInt(routeId, 10);

  return {
    origin: nodeId,
    routeIndex,
    vantage_point: vp_key,
    as_path,
    is_pruned,
    prefix,
    dataset: vpInfo.dataset,
    collector: vpInfo.collector,
    peer_asn: vpInfo.peer_asn,
    peer_ip: vpInfo.peer_ip
  };
};

/**
 * Add hop information to a node
 */
const addHopToNode = (node, routeId, route, prefix, prefixNode) => {
  const hop = createHop(node.id, routeId, route, prefix);

  // Determine if this route is pruned specifically at this node.
  // A route is "pruned here" when it's the leftmost node in the graph tree
  // for this route — i.e., the next upstream hop in as_path doesn't exist
  // in this node's upstream_asns in the pruned tree.
  if (route.is_pruned && route.as_path) {
    const idx = route.as_path.indexOf(node.id);
    if (idx > 0) {
      const nextUpstream = route.as_path[idx - 1];
      const nodeUpstreams = prefixNode?.asn_nodes?.[node.id]?.upstream_asns;
      hop.is_pruned_here = nodeUpstreams ? !(nextUpstream in nodeUpstreams) : false;
    } else {
      // idx === 0: VP peer, full path is represented; idx === -1: node not found
      hop.is_pruned_here = false;
    }
  } else {
    hop.is_pruned_here = false;
  }

  node.hops.push(hop);

  // Add to group (groups routes by prefix)
  if (!node.group[prefix]) {
    node.group[prefix] = {};
  }
  node.group[prefix][routeId] = true;

  // Update pruned status — only when the route is pruned AT this node
  if (hop.is_pruned_here) {
    node.pruned = true;
  }

  // Mark node as VP if this node is the first ASN in the AS path (the VP peer ASN)
  if (route.as_path[0] === node.id) {
    node.isVp = true;
  }
};

/**
 * Create or get an ASN node at a given depth
 * Depth will be converted to column later (depth 0 = rightmost column)
 */
const getOrCreateASNNode = (nodes, asn, depth, maxDepthRef) => {
  if (!nodes.has(asn)) {
    nodes.set(asn, {
      id: asn,
      type: 'asn',
      depth, // Temporary, will be converted to column
      hops: [],
      group: {},
      from: {},
      isVp: false,
      pruned: false
    });

    // Track max depth for column calculation
    if (depth > maxDepthRef.value) {
      maxDepthRef.value = depth;
    }
  }

  return nodes.get(asn);
};

/**
 * Create a link between two nodes
 */
const createLink = (links, nodes, fromId, toId, routeId, route, prefix) => {
  if (!links[fromId]) {
    links[fromId] = {};
  }

  if (!links[fromId][toId]) {
    links[fromId][toId] = { toHops: [], fromHops: [] };
  }

  const hop = createHop(toId, routeId, route, prefix);
  const fromHop = { ...hop, origin: fromId };

  links[fromId][toId].toHops.push(hop);
  links[fromId][toId].fromHops.push(fromHop);

  // Track 'from' relationship on destination node (only for ASN nodes)
  const toNode = nodes.get(toId);
  if (toNode && toNode.from && !toNode.from[fromId]) {
    toNode.from[fromId] = true;
  }
};

/**
 * Recursively walk upstream ASNs from a given ASN
 *
 * @param {object} params - Walk parameters
 * @param {string} params.asn - Current ASN to process
 * @param {number} params.depth - Current depth in tree (distance from origin)
 * @param {string} params.parentAsn - Parent ASN (one level closer to origin)
 * @param {string} params.prefix - Current prefix being processed
 * @param {object} params.prefixNode - Prefix node containing asn_nodes
 * @param {object} params.routes - Route details from backend
 * @param {Map} params.nodes - Graph nodes being built
 * @param {object} params.links - Graph links being built
 * @param {Set} params.processedASNs - Tracks visited ASNs to prevent loops
 * @param {Set} params.processedRoutes - Tracks processed routes
 * @param {Set} params.upstreamASNsSet - Set of upstream ASNs
 * @param {object} params.maxDepthRef - Reference to max depth value
 * @param {Set} params.pathASNs - Set of ASNs in the current path (for cycle detection)
 */
const walkUpstreamASN = ({
  asn,
  depth,
  parentAsn,
  prefix,
  prefixNode,
  routes,
  nodes,
  links,
  processedASNs,
  processedRoutes,
  upstreamASNsSet,
  maxDepthRef,
  pathASNs = new Set()
}) => {
  // Prevent infinite recursion with maximum depth
  const MAX_DEPTH = 50;
  if (depth > MAX_DEPTH) {
    return;
  }

  // Prevent cycles in the current path
  if (pathASNs.has(asn)) {
    return;
  }

  // Prevent infinite loops - don't revisit the same ASN globally
  if (processedASNs.has(asn)) {
    return;
  }
  processedASNs.add(asn);

  // Get ASN node from the prefix node's asn_nodes
  const asnNode = prefixNode.asn_nodes[asn];
  if (!asnNode) {
    return;
  }

  // Check if this ASN has any routes for this prefix
  const matchingRoutes = asnNode.route_ids.filter((routeId) => {
    const route = routes[routeId];
    return route !== undefined;
  });

  // Only create node if it has matching routes
  if (matchingRoutes.length === 0) {
    return;
  }

  // Create or get graph node for this ASN at this depth
  const graphNode = getOrCreateASNNode(nodes, asn, depth, maxDepthRef);

  // Process routes for this prefix
  matchingRoutes.forEach((routeId) => {
    const route = routes[routeId];

    // Add hop metadata to this node
    addHopToNode(graphNode, routeId, route, prefix, prefixNode);
    processedRoutes.add(routeId);

    // Create link from parent ASN to this ASN
    if (parentAsn) {
      createLink(links, nodes, parentAsn, asn, routeId, route, prefix);
    }
  });

  // Recursively walk to upstream ASNs (further from origin)
  const newPathASNs = new Set(pathASNs);
  newPathASNs.add(asn);

  Object.keys(asnNode.upstream_asns).forEach((upstreamAsn) => {
    upstreamASNsSet.add(upstreamAsn);
    walkUpstreamASN({
      asn: upstreamAsn,
      depth: depth + 1,
      parentAsn: asn,
      prefix,
      prefixNode,
      routes,
      nodes,
      links,
      processedASNs,
      processedRoutes,
      upstreamASNsSet,
      maxDepthRef,
      pathASNs: newPathASNs
    });
  });
};

/**
 * Process a single prefix node and walk its origin ASNs
 */
const processPrefix = ({
  prefix,
  selectedGraph,
  routes,
  nodes,
  links,
  processedRoutes,
  processedASNs,
  upstreamASNsSet,
  routePaths,
  origins,
  maxDepthRef
}) => {
  const prefixNode = selectedGraph.prefix_nodes[prefix];
  if (!prefixNode) {
    return;
  }

  // Create prefix node
  if (!nodes.has(prefix)) {
    nodes.set(prefix, {
      id: prefix,
      type: 'prefix',
      hops: [],
      group: {}
    });
  }

  const graphPrefixNode = nodes.get(prefix);

  // Add hops from prefix's routes
  prefixNode.route_ids.forEach((routeId) => {
    const route = routes[routeId];
    if (!route) {
      return;
    }

    const { vp_key, as_path } = route;
    const vpInfo = parseVpKey(vp_key);
    const routeIndex = parseInt(routeId, 10);

    const prefixHop = {
      routeIndex,
      vantage_point: vp_key,
      as_path,
      prefix,
      dataset: vpInfo.dataset,
      collector: vpInfo.collector,
      peer_asn: vpInfo.peer_asn,
      peer_ip: vpInfo.peer_ip
    };

    graphPrefixNode.hops.push(prefixHop);

    // Capture the full AS path (origin last) for upstream-leak detection.
    if (Array.isArray(as_path) && as_path.length) {
      routePaths.push(as_path);
    }

    // Add to group
    if (!graphPrefixNode.group[prefix]) {
      graphPrefixNode.group[prefix] = {};
    }
    graphPrefixNode.group[prefix][routeIndex] = true;

    processedRoutes.add(routeId);
  });

  // Walk to origin ASNs for this prefix (depth 0 - closest to prefix)
  Object.entries(prefixNode.origin_asns).forEach(([originAsn, originInfo]) => {
    // Store origin metadata for color coding
    origins[originAsn] = {
      rpki_status: originInfo.rpki_status,
      vp_count: originInfo.vp_count
    };

    // Create origin node at depth 0 (will be rightmost column)
    const originNode = getOrCreateASNNode(nodes, originAsn, 0, maxDepthRef);

    // Add routes to origin node and create links to prefix
    const asnNode = prefixNode.asn_nodes[originAsn];
    if (!asnNode) {
      return;
    }

    asnNode.route_ids.forEach((routeId) => {
      const route = routes[routeId];
      addHopToNode(originNode, routeId, route, prefix, prefixNode);
      createLink(links, nodes, originAsn, prefix, routeId, route, prefix);
      processedRoutes.add(routeId);
    });

    // Walk upstream from origin (depth 1+ - further left)
    Object.keys(asnNode.upstream_asns).forEach((upstreamAsn) => {
      upstreamASNsSet.add(upstreamAsn);
      walkUpstreamASN({
        asn: upstreamAsn,
        depth: 1,
        parentAsn: originAsn,
        prefix,
        prefixNode,
        routes,
        nodes,
        links,
        processedASNs,
        processedRoutes,
        upstreamASNsSet,
        maxDepthRef
      });
    });
  });
};

/**
 * Convert depth values to column numbers
 * Origins (depth 0) should be rightmost, upstreams increase leftward
 */
const convertDepthsToColumns = (nodes, maxDepth) => {
  nodes.forEach((node) => {
    if (node.type === 'asn' && node.depth !== undefined) {
      node.column = maxDepth - node.depth;
      delete node.depth;
    }
  });
  return maxDepth + 1; // Total number of columns
};

/**
 * Add virtual nodes for links that span multiple columns
 * Virtual nodes help with graph layout and rendering
 */
const addVirtualNodes = (nodes, links, numberOfColumns) => {
  Object.keys(links).forEach((fromId) => {
    const fromNode = nodes.get(fromId);
    if (!fromNode) {
      return;
    }

    Object.keys(links[fromId]).forEach((toId) => {
      const toNode = nodes.get(toId);
      if (!toNode) {
        return;
      }

      const toColumn = toNode.type === 'prefix' ? numberOfColumns : toNode.column;
      const columnSpan = toColumn - fromNode.column;

      if (columnSpan > 1) {
        const virtualId = `${fromNode.id}_${toNode.id}`;

        for (let i = fromNode.column + 1; i < toColumn; i += 1) {
          const virtualNodeId = `${virtualId}_${i}`;
          if (!nodes.has(virtualNodeId)) {
            nodes.set(virtualNodeId, {
              id: virtualNodeId,
              column: i,
              type: 'virtual',
              group: fromNode.group
            });
          }
        }
      }
    });
  });
};

/**
 * Build column structure for graph layout
 * Returns object mapping column index to array of node IDs
 */
const buildColumnStructure = (nodes, numberOfColumns) => {
  const columns = {};

  // Initialize all columns
  for (let i = 0; i < numberOfColumns; i++) {
    columns[i] = [];
  }

  // Assign nodes to columns (only ASN and virtual nodes, not prefixes)
  nodes.forEach((node) => {
    if (node.type === 'asn' || node.type === 'virtual') {
      columns[node.column].push(node.id);
    }
  });

  return columns;
};

/**
 * Build column keys array (indices of non-empty columns)
 */
const buildColumnKeys = (columns, numberOfColumns) => {
  const columnKeys = [];
  for (let i = 0; i < numberOfColumns; i++) {
    if (columns[i] && columns[i].length > 0) {
      columnKeys.push(i);
    }
  }
  return columnKeys;
};

/**
 * Main function to build graph structure from BGP data
 *
 * @param {object} params - Build parameters
 * @param {object} params.selectedGraph - Graph snapshot from backend
 * @param {object} params.routes - Route details from backend
 * @param {string[]} params.prefixes - Selected prefixes to include (empty = all)
 * @returns {object} Graph structure with nodes, links, columns, origins, upstreams
 */
export const buildGraphStructure = ({ selectedGraph, routes, prefixes }) => {
  // Convert MobX observables to plain JS to prevent recursion issues
  const plainSelectedGraph = toJS(selectedGraph);
  const plainRoutes = toJS(routes);

  // Initialize data structures
  const processedRoutes = new Set();
  const processedASNs = new Set();
  const origins = {};
  const upstreamASNsSet = new Set();
  const routePaths = [];
  const nodes = new Map();
  const links = {};
  const maxDepthRef = { value: 0 }; // Use object for pass-by-reference

  // Determine which prefixes to include
  const prefixesToInclude =
    !prefixes || prefixes.length === 0
      ? Object.keys(plainSelectedGraph.prefix_nodes)
      : prefixes.filter((p) => plainSelectedGraph.prefix_nodes[p]);

  // Walk the tree for each selected prefix
  prefixesToInclude.forEach((prefix) => {
    processPrefix({
      prefix,
      selectedGraph: plainSelectedGraph,
      routes: plainRoutes,
      nodes,
      links,
      processedRoutes,
      processedASNs,
      upstreamASNsSet,
      routePaths,
      origins,
      maxDepthRef
    });
  });

  // Convert depths to columns (reverse so origins are on right)
  const numberOfColumns = convertDepthsToColumns(nodes, maxDepthRef.value);

  // Add virtual nodes for multi-column spanning links
  addVirtualNodes(nodes, links, numberOfColumns);

  // Build column structure for layout
  const columns = buildColumnStructure(nodes, numberOfColumns);
  const columnKeys = buildColumnKeys(columns, numberOfColumns);

  // Convert nodes Map to plain object
  const nodesObj = {};
  nodes.forEach((node, id) => {
    nodesObj[id] = node;
  });

  // Extract visible ASNs for color coding
  const visibleASNs = Object.keys(nodesObj).filter((id) => nodesObj[id].type === 'asn');

  // Convert upstreams Set to array
  const upstreamASNs = Array.from(upstreamASNsSet);

  return {
    nodes: nodesObj,
    links,
    columns,
    numberOfColumns,
    columnKeys,
    visibleASNs,
    origins,
    upstreamASNs,
    routePaths
  };
};
